import { useMentionData } from '../../context/MentionContext'
import { useEngagementInteraction } from '../../../hooks/engagement/useEngagementInteraction'

type UseMentionInteractionProps = {
  expanded: boolean
  onManualExpansion?: (mentionId: string | null) => void
  onExpandNextMention?: (
    fromMentionId?: string,
    onlyRepliable?: boolean,
  ) => void
  onExpandPreviousMention?: () => void
  onHoverReset?: () => void
}

type UseMentionInteractionReturn = {
  handleMentionClick: (e: React.MouseEvent) => void
  handleExpandNextFromAction: () => void
  handleCollapse: () => void
  handleKeyDown: (event: React.KeyboardEvent) => void
}

/**
 * Click and keyboard handling for a `MentionCard`: expand on click/Enter/Space,
 * collapse on Escape, and ArrowDown / ArrowUp to move between cards.
 *
 * Thin wrapper over the shared `useEngagementInteraction`: reads the mention id
 * from context and delegates. Clicking an already-expanded card is a no-op
 * (`ignoreClickWhenExpanded`) — collapse only happens via Escape, an outside
 * click, or opening a different card. This keeps clicks inside the expanded
 * reply form (textarea, footer buttons) from collapsing it.
 */
export const useMentionInteraction = ({
  expanded,
  onManualExpansion,
  onExpandNextMention,
  onExpandPreviousMention,
  onHoverReset,
}: UseMentionInteractionProps): UseMentionInteractionReturn => {
  const mention = useMentionData()

  const {
    handleClick,
    handleExpandNextFromAction,
    handleCollapse,
    handleKeyDown,
  } = useEngagementInteraction({
    id: mention.id,
    expanded,
    onManualExpansion,
    onExpandNext: onExpandNextMention,
    onExpandPrevious: onExpandPreviousMention,
    onHoverReset,
    // Embedded controls handle their own clicks: the reaction below the body
    // and the header's hover actions (open on network / copy link).
    reactionSelector: '[data-mention-reaction], [data-mention-actions]',
    ignoreClickWhenExpanded: true,
  })

  return {
    handleMentionClick: handleClick,
    handleExpandNextFromAction,
    handleCollapse,
    handleKeyDown,
  }
}
